<template>
   <div>
      <NavbarComponent />
      <main>
         <div
            class="relative pt-16 pb-32 flex content-center items-center justify-center min-header"
         >
            <div
               class="absolute top-0 w-full h-full bg-center bg-cover"
               :style="{
                  backgroundImage: `url(${headerImage})`,
               }"
            >
               <span
                  id="blackOverlay"
                  class="w-full h-full absolute opacity-75 bg-black"
               ></span>
            </div>

            <div class="container relative mx-auto">
               <div class="items-center flex flex-wrap">
                  <div
                     class="w-full lg:w-6/12 px-4 ml-auto mr-auto text-center"
                  >
                     <div class="pr-12">
                        <h1 class="text-white font-semibold text-5xl">
                           WaveData Cloud
                        </h1>
                        <p class="mt-4 text-lg text-white">
                           Cloud fleet monitoring and remote pump control for industrial
                           operations—view and operate DVCommand and Datavan MPC from anywhere.
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <section class="pb-20 bg-gray-200 -mt-24">
            <div class="container mx-auto px-4">
               <div class="flex flex-wrap">
                  <div class="w-full md:w-4/12 px-4 text-center">
                     <div
                        class="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg"
                     >
                        <div class="px-4 py-5 flex-auto">
                           <div
                              class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-red-400"
                           >
                              <i class="fa-solid fa-cloud text-xl"></i>
                           </div>
                           <h6 class="text-xl font-semibold">
                              Cloud-Based Solution
                           </h6>
                           <p class="mt-2 mb-4 text-gray-600">
                              Secure cloud-based platform for fleet monitoring and
                              remote pump control—including remote view and operation
                              with Datavan MPC and DVCommand
                           </p>
                        </div>
                     </div>
                  </div>

                  <div class="w-full md:w-4/12 px-4 text-center">
                     <div
                        class="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg"
                     >
                        <div class="px-4 py-5 flex-auto">
                           <div
                              class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-red-400"
                           >
                              <i class="fa-solid fa-truck text-xl"></i>
                           </div>
                           <h6 class="text-xl font-semibold">
                              Real-Time Fleet Monitoring
                           </h6>
                           <p class="mt-2 mb-4 text-gray-600">
                              Powerful dashboards and widgets for monitoring
                              your fleet and individual equipment
                           </p>
                        </div>
                     </div>
                  </div>

                  <div class="w-full md:w-4/12 px-4 text-center">
                     <div
                        class="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg"
                     >
                        <div class="px-4 py-5 flex-auto">
                           <div
                              class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-red-400"
                           >
                              <i class="fa-solid fa-server text-xl"></i>
                           </div>
                           <h6 class="text-xl font-semibold">
                              Data Management
                           </h6>
                           <p class="mt-2 mb-4 text-gray-600">
                              Time series data storage, historical graphing,
                              file repository, alarms and events tracking.
                           </p>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- AI Features Highlight -->
               <div class="mt-12 bg-white rounded-xl shadow-lg overflow-hidden">
                  <div class="flex flex-col lg:flex-row">
                     <div
                        class="w-full lg:w-5/12 items-center justify-center"
                     >
                        <img
                           src="../assets/img/dataPipes.jpg"
                           alt="Data Pipeline Architecture"
                           style="width: 100%; height: auto"
                           class="rounded-lg"
                        />
                     </div>
                     <div class="w-full lg:w-7/12 p-6">
                        <p class="text-lg text-gray-700 mb-4">
                           Unlock the full potential of your field data with
                           <strong
                              >WaveData's intelligent cloud file
                              management</strong
                           >. Seamlessly share, store, and auto-upload files of
                           any type from our webapp, DVCommand, or any storage
                           folders or via our API.
                        </p>
                        <p class="text-lg text-gray-700 mb-4">
                           Our enhanced file repository uses
                           <strong>cutting-edge AI</strong> to automatically
                           classify, summarize, and generate searchable keywords
                           for your documents and images (even those captured
                           with your webcam or phone!).
                        </p>

                        <p class="text-lg text-gray-700">
                           Effortlessly access organized insights with
                           AI-powered executive summaries, key aspects, data
                           summaries, and more, transforming your raw data and
                           files into readily available knowledge.
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- Slider section -->
         <section class="py-20 bg-white">
            <div class="container mx-auto px-4">
               <div class="relative bg-white rounded-lg overflow-hidden">
                  <!-- Slides -->
                  <div class="min-h-[600px]">
                     <div
                        v-for="(slide, index) in slides"
                        :key="index"
                        class="relative"
                        :style="{
                           opacity: currentSlide === index ? 1 : 0,
                           visibility:
                              currentSlide === index ? 'visible' : 'hidden',
                           transition: 'opacity 500ms ease-in-out',
                           position:
                              currentSlide === index ? 'relative' : 'absolute',
                           top: 0,
                           left: 0,
                           width: '100%',
                        }"
                     >
                        <div class="grid md:grid-cols-2 gap-12 p-12">
                           <div class="flex items-center justify-center">
                              <div class="w-full">
                                 <img
                                    :src="slide.image"
                                    :alt="slide.title"
                                    class="w-full h-auto max-h-[400px] object-contain mx-auto rounded-lg"
                                 />
                              </div>
                           </div>
                           <div
                              class="flex flex-col justify-center pl-4 bg-gray-100 rounded-lg p-8"
                           >
                              <h3
                                 class="text-3xl font-semibold mb-6 text-gray-800"
                              >
                                 {{ slide.title }}
                              </h3>
                              <p class="text-xl text-gray-600 leading-relaxed">
                                 {{ slide.description }}
                              </p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Navigation controls -->
                  <div class="flex justify-center items-center gap-4 mt-4 pb-4">
                     <button
                        @click="prevSlide"
                        class="bg-gray-800/20 hover:bg-gray-800/30 text-gray-800 w-10 h-10 rounded-full flex items-center justify-center text-2xl transition-all duration-200 focus:outline-none"
                        aria-label="Previous slide"
                     >
                        ‹
                     </button>

                     <!-- Navigation dots -->
                     <div class="flex justify-center items-center space-x-4">
                        <div
                           v-for="(_, index) in slides"
                           :key="index"
                           @click="goToSlide(index)"
                           class="dots rounded-full cursor-pointer transition-colors duration-200"
                           :style="{
                              backgroundColor:
                                 currentSlide === index ? '#000000' : '#D1D5DB',
                              border:
                                 '2px solid ' +
                                 (currentSlide === index
                                    ? '#000000'
                                    : '#D1D5DB'),
                           }"
                        ></div>
                     </div>

                     <button
                        @click="nextSlide"
                        class="bg-gray-800/20 hover:bg-gray-800/30 text-gray-800 w-10 h-10 rounded-full flex items-center justify-center text-2xl transition-all duration-200 focus:outline-none"
                        aria-label="Next slide"
                     >
                        ›
                     </button>
                  </div>
               </div>
            </div>
         </section>

         <!-- Pricing Tiers Section -->
         <section class="py-20 bg-gray-100">
            <div class="container mx-auto px-4">
               <div class="text-center mb-16">
                  <h2 class="text-4xl font-semibold">
                     WaveData Services: Choose the Tier That Fits Your Needs
                  </h2>
                  <p class="text-xl text-gray-600 mt-4">
                     Our WaveData services provide flexible solutions for
                     accessing, managing, and visualizing your valuable
                     equipment information.
                  </p>
               </div>

               <div class="flex flex-nowrap justify-between gap-4 mx-auto">
                  <!-- Tier LITE -->
                  <div
                     class="w-1/3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow"
                  >
                     <div class="text-center">
                        <h3
                           class="text-3xl font-bold mb-2 flex items-center justify-center gap-4"
                        >
                           <span class="text-gray-500 mr-1">Tier</span>
                           <span style="color: #1d4ed8">LITE</span>
                        </h3>
                        <p class="text-gray-600 mb-8">
                           Perfect for basic data collection needs
                        </p>
                     </div>

                     <div class="h-px w-full bg-blue-200 mb-8"></div>

                     <p class="text-gray-600 mb-6">
                        Perfect for users who need simple, straightforward
                        access to their sensor data. Works with any existing
                        data collection system creating sensor log files,
                        including our DVCommand.
                     </p>

                     <ul class="space-y-4">
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-blue-700"
                                 >Data Files:</span
                              >
                              <p class="text-gray-600">
                                 Automated file uploaders detect and upload
                                 sensor data to our cloud, converting DVCommand
                                 data files automatically into simple CSV format
                                 for easy integration.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-blue-700"
                                 >Manual User Uploads:</span
                              >
                              <p class="text-gray-600">
                                 Conveniently upload documents and files through
                                 our user-friendly interface.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-blue-700"
                                 >Camera Pictures:</span
                              >
                              <p class="text-gray-600">
                                 Store and access associated camera pictures
                                 alongside your sensor data.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-blue-700"
                                 >Cloud Storage:</span
                              >
                              <p class="text-gray-600">
                                 All uploaded data files and images are securely
                                 stored in the cloud, accessible from anywhere.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-blue-700"
                                 >Easy Download:</span
                              >
                              <p class="text-gray-600">
                                 Download your data files and images quickly and
                                 easily whenever needed.
                              </p>
                           </div>
                        </li>
                     </ul>
                  </div>

                  <!-- Tier PRO -->
                  <div
                     class="w-1/3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-xl p-8 hover:shadow-2xl transition-shadow"
                  >
                     <div class="text-center">
                        <h3
                           class="text-3xl font-bold mb-2 flex items-center justify-center gap-4"
                        >
                           <span class="text-gray-500 mr-1">Tier</span>
                           <span style="color: #7e22ce">PRO</span>
                        </h3>
                        <p class="text-gray-600 mb-8">
                           Ideal for growing operations and analytics needs
                        </p>
                     </div>

                     <div class="h-px w-full bg-purple-200 mb-8"></div>

                     <p class="text-gray-600 mb-6">
                        Designed for users who want comprehensive data access,
                        database archiving for historical analysis, and charting
                        / visualization capabilities.
                     </p>

                     <ul class="space-y-4">
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-purple-700"
                                 >All LITE Features</span
                              >
                              <p class="text-gray-600">
                                 Includes all features from the LITE tier as
                                 foundation.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-purple-700"
                                 >Database Storage:</span
                              >
                              <p class="text-gray-600">
                                 All wave sensor data automatically stored in a
                                 robust and scalable database for efficient
                                 querying and retrieval.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-purple-700"
                                 >Full Historical Graphing:</span
                              >
                              <p class="text-gray-600">
                                 Visualize sensor data trends over time with
                                 interactive and customizable graphs. Analyze
                                 historical patterns and identify key events.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-purple-700"
                                 >Charting Capabilities:</span
                              >
                              <p class="text-gray-600">
                                 Create variety of charts including line charts,
                                 bar charts, and scatter plots to gain deeper
                                 insights.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-purple-700"
                                 >Automated Data Ingestion:</span
                              >
                              <p class="text-gray-600">
                                 Optional automated data ingestion configuration
                                 available, depending on sensor compatibility.
                              </p>
                           </div>
                        </li>
                     </ul>
                  </div>

                  <!-- Tier COMPLETE -->
                  <div
                     class="w-1/3 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow"
                  >
                     <div class="text-center">
                        <h3
                           class="text-3xl font-bold mb-2 flex items-center justify-center gap-4"
                        >
                           <span class="text-gray-500 mr-1">Tier</span>
                           <span style="color: #047857">COMPLETE</span>
                        </h3>
                        <p class="text-gray-600 mb-8">
                           For enterprise & complete monitoring needs
                        </p>
                     </div>

                     <div class="h-px w-full bg-emerald-200 mb-8"></div>

                     <p class="text-gray-600 mb-6">
                        The ultimate solution for users who require live data
                        streams, real-time insights, alarms and notifications,
                        and a fully customized monitoring experience.
                     </p>

                     <ul class="space-y-4">
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-emerald-700"
                                 >All PRO Features</span
                              >
                              <p class="text-gray-600">
                                 Includes all features from the PRO tier as
                                 foundation.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-emerald-700"
                                 >Full Live Data:</span
                              >
                              <p class="text-gray-600">
                                 Access real-time data streams directly from
                                 your connected equipment sensors, monitoring
                                 current conditions as they happen.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-emerald-700"
                                 >Custom Screens & Dashboards:</span
                              >
                              <p class="text-gray-600">
                                 Design personalized monitoring screens and
                                 comprehensive dashboards that combine real-time
                                 data feeds, historical charts, and key metrics
                                 in a unified view.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-emerald-700"
                                 >Real-Time Integration:</span
                              >
                              <p class="text-gray-600">
                                 Seamless integration with compatible real-time
                                 sensor equipment for immediate data access.
                              </p>
                           </div>
                        </li>
                        <li class="flex items-start">
                           <i
                              class="fa-solid fa-check text-emerald-500 mt-1 mr-3"
                           ></i>
                           <div>
                              <span class="font-semibold text-emerald-700"
                                 >Full Automation:</span
                              >
                              <p class="text-gray-600">
                                 Complete automated data ingestion support for
                                 all compatible sensors, with comprehensive
                                 historical analysis capabilities.
                              </p>
                           </div>
                        </li>
                     </ul>
                  </div>
               </div>

               <!-- Contact CTA -->
               <div class="text-center mt-8 mb-8 py-20">
                  <h2 class="text-4xl font-bold text-[#2b3b66] mb-6">
                     Ready to Transform Your Data Experience?
                  </h2>
                  <p class="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
                     Schedule a personalized demo to see how WaveData can
                     revolutionize your industrial operations monitoring.
                  </p>
                  <button
                     @click="scrollToContact"
                     class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                     Book a Demo
                  </button>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo } from "@/utils/seo";
import headerImage from "@/assets/img/ami_wave_pic.jpg";
// Import all screenshots
import fleetviewImage from "../assets/screenshots/fleetview.jpg";
import fleetDrilldownImage from "../assets/screenshots/fleet-drilldown.png";
import historicalImage from "../assets/screenshots/historical.jpg";
import dataexportImage from "../assets/screenshots/dataexport.jpg";
import customScreensImage from "../assets/screenshots/customScreens.png";
import customAlertsImage from "../assets/screenshots/customAlerts.png";
import pumpMaintenanceImage from "../assets/screenshots/pumpMaintenance.png";
import dataPipesImage from "../assets/img/dataPipes.jpg";

export default {
   name: "WaveData",
   data() {
      return {
         headerImage,
         dataPipesImage,
         currentSlide: 0,
         slides: [
            {
               title: "Monitor Your Assets, Anywhere, Anytime",
               description:
                  "Gain flexible remote access to critical equipment data. Stay informed with fleet summaries and detailed equipment views, all from your fingertips.",
               image: fleetviewImage,
            },
            {
               title: "Visualize Your Data Your Way",
               description:
                  "Build custom dashboards with a wide array of widgets—meters, charts, images, and more. Tailor your display for desktop or mobile, ensuring optimal accessibility.",
               image: fleetDrilldownImage,
            },
            {
               title: "Historical Insights, Real-Time Action",
               description:
                  "Analyze historical sensor data with interactive charts and stay ahead of issues with instant alarm notifications and event logs.",
               image: historicalImage,
            },
            {
               title: "Data at Your Fingertips",
               description:
                  "Export timestamped sensor data and events to CSV for in-depth analysis. Create custom reports from your dashboards to share key insights.",
               image: dataexportImage,
            },
            {
               title: "Customizable Dashboards",
               description:
                  "Build custom dashboards with a wide array of widgets—meters, charts, images, and more. Tailor your display for desktop or mobile, ensuring optimal accessibility.",
               image: customScreensImage,
            },
            {
               title: "Smart Alarms, Instant Alerts",
               description:
                  "Set custom alarm rules to receive SMS/email notifications based on sensor triggers, ensuring proactive maintenance and minimizing downtime.",
               image: customAlertsImage,
            },
            {
               title: "Equipment Health at a Glance",
               description:
                  "Visualize equipment performance with customizable dashboards, including real-time meters, charts, and alarm indicators.",
               image: pumpMaintenanceImage,
            },
         ],
         slideInterval: null,
      };
   },
   components: {
      NavbarComponent,
      FooterComponent,
   },
   mounted() {
      setPageSeo({
         title: "WaveData Cloud | Fleet Monitoring & Remote Pump Control | AMI",
         description:
            "WaveData Cloud provides fleet monitoring, remote pump control, and industrial analytics—secure cloud access to DVCommand and Datavan MPC from anywhere.",
         keywords:
            "WaveData Cloud, fleet monitoring, remote pump control, remote monitoring, frac data cloud, industrial IoT, Datavan MPC, DVCommand, AMI",
      });
      this.startSlideshow();
   },
   beforeUnmount() {
      if (this.slideInterval) {
         clearInterval(this.slideInterval);
      }
   },
   methods: {
      startSlideshow() {
         this.slideInterval = setInterval(() => {
            this.nextSlide();
         }, 10000); // Changed to 10 seconds
      },
      nextSlide() {
         this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      },
      prevSlide() {
         this.currentSlide =
            (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      },
      goToSlide(index) {
         this.currentSlide = index;
      },
      resetSlideTimer() {
         if (this.slideInterval) {
            clearInterval(this.slideInterval);
            this.startSlideshow();
         }
      },
      scrollToContact() {
         // First navigate to home page
         this.$router.push("/").then(() => {
            // Wait for the next tick to ensure the page has loaded
            this.$nextTick(() => {
               // Find the contact section and scroll to it
               const contactSection = document.getElementById("contact");
               if (contactSection) {
                  contactSection.scrollIntoView({ behavior: "smooth" });
               }
            });
         });
      },
   },
   watch: {
      currentSlide() {
         // Reset the timer whenever slide changes manually
         this.resetSlideTimer();
      },
   },
};
</script>

<style>
.min-header {
   min-height: 45vh;
}

/* Navigation button symbols */
button[aria-label="Previous slide"],
button[aria-label="Next slide"] {
   font-family: system-ui, -apple-system;
   min-width: 5rem;
   display: flex;
   align-items: center;
   justify-content: center;
}
.dots {
   margin-top: 6px;
   width: 10px;
   height: 10px;
   border-radius: 50%;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}
</style> 